import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// AG-Grid
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { AgGridReact } from 'ag-grid-react';

// AG-Grid Components
import CustomShareChgComponent from './components/CustomShareChgComponent';
import CustomShareChgComponentNoUnit from './components/CustomShareChgComponentNoUnit';
import RCACellComponent from './components/RCACellComponent';
import CustomColumnHeader from './components/CustomColumnHeader';
import CustomColumnMainHeader from './components/CustomColumnMainHeader';
import CustomNoRowsOverlay from './components/CustomNoRowsTemplate';
import CustomDateComponent from './components/CustomDateComponent';
import CustomBadgeComponent from './components/CustomBadgeComponent';
import StoreDrilldownRowOverlay from './components/StoreDrilldownRowOverlay';

// Custom Styles
import styles from './DVTable.module.css';

// AG-Grid License
LicenseManager.setLicenseKey(
	'Using_this_AG_Grid_Enterprise_key_( AG-049209 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Walmart Inc. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 4 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 13 November 2024 )____[v2]_MTczMTQ1NjAwMDAwMA==1beafb88faad7992e95a6219f756fefb'
);

import { Pagination } from '@walmart-dataventures/shared-components';
import SomethingWentWrong from '../../common/components/SomethingWentWrong';

const DVTable = ({
	columns,
	data,
	handleCellClicked,
	filters,
	tableRef,
	onGridReady,
	tab,
	anomalyType,
	isStoreDrilldown = false,
	isError = false,
	initialItemsPerPage,
}) => {
	// Custom No Rows Overlay Template
	const noRowsOverlayComponent = useMemo(() => {
		return isError
			? SomethingWentWrong
			: isStoreDrilldown
			? StoreDrilldownRowOverlay
			: CustomNoRowsOverlay;
	}, [isError]);

	// Custom Cell Renderers
	const components = useMemo(
		() => ({
			customColumnHeader: CustomColumnHeader,
			customColumnMainHeader: CustomColumnMainHeader,
			customShareChgComponent: CustomShareChgComponent,
			CustomShareChgComponentNoUnit: CustomShareChgComponentNoUnit,
			rcaCellComponent: RCACellComponent,
			customDateComponent: CustomDateComponent,
			customBadgeComponent: CustomBadgeComponent,
		}),
		[]
	);

	const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
	const [page, setPage] = useState(1);
	const [totalItems, setTotalItems] = useState(data?.length || 0);

	let gridOptions = {
		rowStyle: { background: 'white' },
		onCellClicked: (e) => {
			if (handleCellClicked) {
				return handleCellClicked(
					{
						...e?.data,
					},
					e
				);
			}
		},
	};

	// A function to demonstrate the no rows overlay case
	// const showNoRowsTemplate = () => {
	// 	tableRef?.current?.api.showNoRowsOverlay();
	// };

	useEffect(() => {
		setItemsPerPage(initialItemsPerPage);
		setPage(1);
		setTotalItems(data?.length || 0);
		gridOptions = {
			...gridOptions,
			paginationPageSize: itemsPerPage,
		};
	}, [columns, data]);

	useEffect(() => {
		applyFilters(filters);
	}, [filters]);

	useEffect(() => {
		if (data && tableRef?.current?.api?.getDisplayedRowCount()) {
			updateRowCount();
		}
	}, [filters, data?.length, tab, anomalyType]);

	const handlePage = (page) => {
		// Since AG-Grid is counting their pages starting from 0, we need to decrease by 1
		const agGridPage = page - 1;

		// Apply the new page index to AG-Grid
		tableRef.current?.api.paginationGoToPage(agGridPage);

		// Set the current page to state
		setPage(page);
	};

	const handleItemsPerPage = (itemsPerPage) => {
		setItemsPerPage(itemsPerPage);
	};

	const updateRowCount = async () => {
		const newCount = await tableRef?.current?.api?.getDisplayedRowCount();
		setTotalItems(newCount || 0);
		// Set back the pagination to first page
		setPage(1);
		// Set back the total visible items to 20
		setItemsPerPage(initialItemsPerPage);
	};

	const applyFilters = (filterModel) => {
		tableRef.current?.api?.setFilterModel(filterModel);
	};

	// To enable setting for all columns use this otherwise use the column defination on individual column
	const defaultColDef = useMemo(() => {
		return {
			suppressMenu: true, // remove the column menu
			minWidth: isStoreDrilldown ? 190 : 150,
			flex: 1,
			resizable: false,
			sortable: true,
			sortingOrder: ['desc', 'asc'],
		};
	}, []);

	return (
		<div>
			<div
				style={{
					width: '100%',
				}}>
				<div
					className="ag-theme-alpine ag-theme-scintilla"
					style={{
						width: '100%',
						height: '100%',
						minHeight: '300px',
					}}>
					<AgGridReact
						modules={[SetFilterModule, ClientSideRowModelModule]}
						data-test={'scintilla-table-component'}
						ref={tableRef}
						domLayout="autoHeight"
						components={components}
						suppressCellFocus={true}
						rowData={data}
						pagination={true}
						columnDefs={data.length > 0 ? columns : []}
						animateRows={false}
						scrollbarWidth={5} // width for horizontal scrollbar
						gridOptions={gridOptions}
						suppressMovableColumns={true}
						suppressMultiSort={true}
						suppressContextMenu={true}
						defaultColDef={defaultColDef}
						suppressPaginationPanel={true}
						paginationAutoPageSize={false}
						paginationPageSize={itemsPerPage}
						onFilterChanged={updateRowCount}
						noRowsOverlayComponent={noRowsOverlayComponent}
						onGridReady={onGridReady}
					/>
				</div>
			</div>
			<div className={styles.insightsPagination}>
				<Pagination
					currentPage={page}
					itemsPerPage={itemsPerPage}
					totalItemCount={totalItems}
					setCurrentPage={handlePage}
					hideItemsPerPageCount={false}
					hideItemsPerPageDropdown={false}
					onItemsPerPageChange={handleItemsPerPage}
				/>
			</div>
		</div>
	);
};
DVTable.propTypes = {
	data: PropTypes.any,
	columns: PropTypes.any,
	handleCellClicked: PropTypes.func,
	filters: PropTypes.object,
	tableRef: PropTypes.func,
	onGridReady: PropTypes.func,
	tab: PropTypes.any,
	anomalyType: PropTypes.any,
	isError: PropTypes.bool,
	isStoreDrilldown: PropTypes.bool,
	initialItemsPerPage: PropTypes.any,
};

export default DVTable;
